import { useMemo } from 'react'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { FormattedMessage, IntlProvider } from 'react-intl'
import { Stack } from '@chakra-ui/react'
import { useRecoilValue } from 'recoil'

import { FormField, LogicDto, MyInfoFormField } from '~shared/types'

import { ThemeColorScheme } from '~theme/foundations/colours'
import { useIsMobile } from '~hooks/useIsMobile'
import Button from '~components/Button'
import InlineMessage from '~components/InlineMessage'
import { FormFieldValues } from '~templates/Field'

import { getLogicUnitPreventingSubmit } from '~features/logic/utils'

import { LanguageFilter } from '../../../../../atom'

interface PublicFormSubmitButtonProps {
  formFields: MyInfoFormField<FormField>[]
  formLogics: LogicDto[]
  colorTheme: string
}

/**
 * This component is split up so that input changes will not rerender the
 * entire FormFields component leading to terrible performance.
 */
export const PublicFormSubmitButton = ({
  formFields,
  formLogics,
  colorTheme,
}: PublicFormSubmitButtonProps): JSX.Element => {
  const isMobile = useIsMobile()
  const { isSubmitting } = useFormState()
  const formInputs = useWatch<FormFieldValues>({})

  const preventSubmissionLogic = useMemo(() => {
    return getLogicUnitPreventingSubmit({
      formInputs,
      formFields,
      formLogics,
    })
  }, [formInputs, formFields, formLogics])

  const locale: any = useRecoilValue(LanguageFilter)
  return (
    <IntlProvider locale={locale}>
      <Stack px={{ base: '1rem', md: 0 }} pt="2.5rem" pb="4rem">
        <Button
          isFullWidth={isMobile}
          w="100%"
          colorScheme={`theme-${colorTheme}` as ThemeColorScheme}
          type="submit"
          isLoading={isSubmitting}
          isDisabled={!!preventSubmissionLogic}
          loadingText={
            locale === 'en'
              ? 'Submitting'
              : locale === 'si'
              ? 'ඉදිරිපත් කරනවා'
              : 'சமர்ப்பிக்கிறது'
          }
        >
          {preventSubmissionLogic
            ? locale === 'en'
              ? 'Submission disabled'
              : locale === 'si'
              ? 'ඉදිරිපත් කිරීම අබල කර ඇත'
              : 'சமர்ப்பிப்பு முடக்கப்பட்டது'
            : locale === 'en'
            ? 'Submit now'
            : locale === 'si'
            ? 'දැන් ඉදිරිපත් කරන්න'
            : 'இப்போது சமர்ப்பிக்கவும்'}
        </Button>
        {preventSubmissionLogic ? (
          <InlineMessage variant="warning">
            {preventSubmissionLogic.preventSubmitMessage}
          </InlineMessage>
        ) : null}
      </Stack>
    </IntlProvider>
  )
}
